// HomeActivityPhotoGroup.jsx

import React, { useMemo } from 'react';

// Utilities
import { formatDateTime } from 'utilities/DateTime';
import { getActivityActorName } from 'node/activity/utils/formatActivitySystemMessages';
import { formatPhotoGroupMessage } from 'home/activity/utils/groupNearbyPhotoItems';
import { getActivityItemTimestampMs } from 'node/activity/utils/groupActivityItemsByDay';

// CSS
import './HomeActivityPhotoGroup.css';

// Components
import ActivityReactionControl from 'node/activity/items/ActivityReactionControl';
import ActivityRelationshipChip from 'node/activity/items/ActivityRelationshipChip';

/**
 * HomeActivityPhotoGroup
 *
 * One Home row for nearby photo uploads: a single line plus a thumb grid.
 *
 * @param {object} props
 * @param {object} props.item - Grouped row from groupNearbyPhotoItems.
 * @param {'datetime'|'time'} [props.timestampStyle]
 * @returns {ReactNode}
 */
const HomeActivityPhotoGroup = React.memo(({
  item,
  timestampStyle = 'datetime',
}) => {
  const photos = item?.items;
  const firstEvent = item?.event || photos?.[0]?.event || null;
  const actorName = getActivityActorName(firstEvent);
  const message = formatPhotoGroupMessage(Array.isArray(photos) ? photos.length : 0);

  const timestampLabel = useMemo(() => {
    const occurredMs = Number.isFinite(item?.at) && item.at > 0
      ? item.at
      : getActivityItemTimestampMs(photos?.[0]);
    if (!occurredMs) return '';
    return formatDateTime(
      new Date(occurredMs),
      timestampStyle === 'time'
        ? { intlOptions: { hour: 'numeric', minute: '2-digit', hour12: true } }
        : undefined
    );
  }, [item?.at, photos, timestampStyle]);

  if (!Array.isArray(photos) || photos.length === 0 || !firstEvent) return null;

  return (
    <div className="home-activity-photo-group-container">

      {/* ROW */}
      <div className="home-activity-photo-group-row">

        {/* MAIN */}
        <div className="home-activity-photo-group-main">

          {/* BODY */}
          <div className="home-activity-photo-group-body">
            <span className="home-activity-photo-group-bullet" aria-hidden="true">•</span>
            <span className="home-activity-photo-group-copy">
              <span className="home-activity-photo-group-actor">{actorName}</span>
              <span className="home-activity-photo-group-message">{message}</span>
            </span>
          </div>

          {/* GRID */}
          <div className="home-activity-photo-group-grid">
            {photos.map((photo) => (
              <div
                key={photo.key}
                className="home-activity-photo-group-thumb-wrapper"
              >
                <ActivityRelationshipChip
                  event={photo.event}
                  variant="grid"
                />
              </div>
            ))}
          </div>
        </div>

        {/* META */}
        <div className="home-activity-photo-group-meta">
          <ActivityReactionControl
            event={firstEvent}
            node={item.node || photos[0]?.node || null}
            reactions={item.reactions || photos[0]?.reactions || []}
          />
          {timestampLabel ? (
            <div className="home-activity-photo-group-time">{timestampLabel}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

HomeActivityPhotoGroup.displayName = 'HomeActivityPhotoGroup';

export default HomeActivityPhotoGroup;
